import { Routes, Route } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import AppLayout from '../layouts/AppLayout'
import ProtectedRoute from './ProtectedRoute'
import PublicRoute from './PublicRoute'
import Loader from '../components/common/Loader'
import NotFound from '../components/common/NotFound'
import { AdminAuthProvider } from '../context/AdminAuthContext'

// Route components are code-split so each screen loads on demand instead of
// shipping in the initial bundle. Guards/layout stay eager (they gate everything).
const OnboardingHome = lazy(() => import('../components/OnboardingHome'))
const Home = lazy(() => import('../components/Home'))
const Dashboard = lazy(() => import('../components/Dashboard'))
const Orders = lazy(() => import('../components/Orders'))
// Phase 16.7C-D: the legacy day-by-day plan builder (`/plans`, `Plans.js`) is
// retired — Subscription V2 (`/subscribe`) is the sole new-subscription path.
// The route is removed so the builder screen is not reachable.
const GymSelect = lazy(() => import('../components/GymSelect'))
const SubscriptionSetup = lazy(() => import('../components/subscription/SubscriptionSetup'))
const Profile = lazy(() => import('../components/Profile'))
const ProductDetails = lazy(() => import('../components/ProductDetails'))
const Cart = lazy(() => import('../components/Cart'))
const Checkout = lazy(() => import('../components/Checkout'))
const AllProducts = lazy(() => import('../components/AllProducts'))
const Payment = lazy(() => import('../components/Payment'))
const PaymentSuccess = lazy(() => import('../components/PaymentSuccess'))
const Subscription = lazy(() => import('../components/Subscription'))
const MealHistory = lazy(() => import('../components/MealHistory'))
const HealthInformation = lazy(() => import('../components/HealthInformation'))
const TermsConditions = lazy(() => import('../components/TermsConditions'))
const PrivacyPolicy = lazy(() => import('../components/PrivacyPolicy'))
const DeleteAccount = lazy(() => import('../components/DeleteAccount'))

// Admin / merchant panel — code-split so it never ships in the customer bundle.
const AdminRoute = lazy(() => import('../admin/AdminRoute'))
const AdminLayout = lazy(() => import('../admin/AdminLayout'))
const AdminLogin = lazy(() => import('../admin/AdminLogin'))
const AdminStaff = lazy(() => import('../admin/pages/AdminStaff'))
const AdminPickups = lazy(() => import('../admin/pages/AdminPickups'))
const AdminDashboard = lazy(() => import('../admin/pages/AdminDashboard'))
const AdminPreparation = lazy(() => import('../admin/pages/AdminPreparation'))
const AdminWorkload = lazy(() => import('../admin/pages/AdminWorkload'))
const AdminCustomers = lazy(() => import('../admin/pages/AdminCustomers'))
const AdminProductQuality = lazy(() => import('../admin/pages/AdminProductQuality'))
const AdminProducts = lazy(() => import('../admin/pages/AdminProducts'))
const AdminRevenue = lazy(() => import('../admin/pages/AdminRevenue'))
const AdminSubscriptionSummary = lazy(() => import('../admin/pages/AdminSubscriptionSummary'))
const AdminDeliveries = lazy(() => import('../admin/pages/AdminDeliveries'))
const AdminMissedMeals = lazy(() => import('../admin/pages/AdminMissedMeals'))
const AdminOrders = lazy(() => import('../admin/pages/AdminOrders'))
const AdminOrderDetails = lazy(() => import('../admin/pages/AdminOrderDetails'))
const AdminSubscriptions = lazy(() => import('../admin/pages/AdminSubscriptions'))
const AdminSubscriptionDetails = lazy(() => import('../admin/pages/AdminSubscriptionDetails'))
const AdminGyms = lazy(() => import('../admin/pages/AdminGyms'))
const AdminGymRequests = lazy(() => import('../admin/pages/AdminGymRequests'))
const AdminSubscriptionSettings = lazy(() => import('../admin/pages/AdminSubscriptionSettings'))

export default function AppRoutes () {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* 🔓 Public Routes (Login) */}
        <Route element={<PublicRoute />}>
          <Route path='/' element={<OnboardingHome />} />
        </Route>

        {/* 🔐 Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path='/home' element={<Home />} />
            <Route path='/dashboard' element={<Dashboard />} />
            <Route path='/orders' element={<Orders />} />
            <Route path='/select-gym' element={<GymSelect />} />
            <Route path='/subscribe' element={<SubscriptionSetup />} />
            <Route path='/profile' element={<Profile />} />
            <Route path='/product-details/:id' element={<ProductDetails />} />
            <Route path='/cart' element={<Cart />} />
            <Route path='/checkout' element={<Checkout />} />
            <Route path='/products' element={<AllProducts />} />
            <Route path='/payment' element={<Payment />} />
            <Route path='/payment-success' element={<PaymentSuccess />} />
            <Route path='/subscriptions' element={<Subscription />} />
            <Route path='/meal-history' element={<MealHistory />} />
            <Route path='/health' element={<HealthInformation />} />
            <Route path='/terms' element={<TermsConditions />} />
            <Route path='/privacy' element={<PrivacyPolicy />} />
            <Route path='/delete-account' element={<DeleteAccount />} />
          </Route>
        </Route>

        {/* 🔒 Admin / merchant panel — INDEPENDENT admin session (Phase 16.4),
            separate from the customer login. AdminAuthProvider resolves the
            `admin_token` cookie for everything under /admin/*; the client gate is
            UX only — the real boundary is `adminAuth` on every /api/admin/* call.
            Its own layout (no customer bottom nav). */}
        <Route element={<AdminAuthProvider />}>
          <Route path='/admin/login' element={<AdminLogin />} />
          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route path='/admin' element={<AdminDashboard />} />
              <Route path='/admin/pickups' element={<AdminPickups />} />
              <Route path='/admin/staff' element={<AdminStaff />} />
            <Route path='/admin/orders' element={<AdminOrders />} />
            <Route path='/admin/orders/:id' element={<AdminOrderDetails />} />
            <Route path='/admin/subscriptions' element={<AdminSubscriptions />} />
            <Route path='/admin/subscriptions/:id' element={<AdminSubscriptionDetails />} />
            <Route path='/admin/products' element={<AdminProducts />} />
            <Route path='/admin/revenue' element={<AdminRevenue />} />
            <Route path='/admin/subscription-summary' element={<AdminSubscriptionSummary />} />
            <Route path='/admin/deliveries' element={<AdminDeliveries />} />
            <Route path='/admin/missed-meals' element={<AdminMissedMeals />} />
            <Route path='/admin/preparation' element={<AdminPreparation />} />
            <Route path='/admin/workload' element={<AdminWorkload />} />
            <Route path='/admin/customers' element={<AdminCustomers />} />
            <Route path='/admin/quality' element={<AdminProductQuality />} />
            <Route path='/admin/gyms' element={<AdminGyms />} />
            <Route path='/admin/gym-requests' element={<AdminGymRequests />} />
            <Route path='/admin/subscription-settings' element={<AdminSubscriptionSettings />} />
            </Route>
          </Route>
        </Route>

        {/* 404 — no blank screens on unknown paths */}
        <Route path='*' element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}
